<template>
  <md-dialog
    :md-active.sync="show"
    :md-close-on-esc="false"
    @md-opened="openDialog"
    @md-clicked-outside="closeDialog"
    @keyup.esc="closeDialog"
    @keyup.enter="changePassword"
  >
    <md-dialog-title>Change Password</md-dialog-title>
    <div class="md-dialog-content md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <form>
        <md-field class="md-layout-item md-small-size-100 md-size-100" :class="oldPasswordClass">
          <label> Current password </label>
          <md-input v-model="passwordData.oldPassword" type="password" required autocomplete="off"></md-input>
          <div class="md-error">
            <div v-for="error in errors.oldPassword" :key="error">{{ error }}</div>
          </div>
        </md-field>
        <md-field class="md-layout-item md-small-size-100 md-size-100 password-layout" :class="newPasswordClass">
          <label> New password </label>
          <md-input v-model="passwordData.newPassword1" type="password" required></md-input>
          <div class="md-error new-pass">
            <div v-for="error in errors.newPassword1" :key="error">{{ error }}</div>
          </div>
        </md-field>
        <password v-model="passwordData.newPassword1" :strengthMeterOnly="true" />
        <md-field
          class="md-layout-item md-small-size-100 md-size-100 md-invalid md-error-multi-line"
          v-bind:class="[newPassword2Class, `md-error-${newPassword2NumErrors}-lines`]"
        >
          <label> Confirm new password </label>
          <md-input v-model="passwordData.newPassword2" type="password" required></md-input>
          <div class="md-error md-error-multi-line">
            <div v-for="error in errors.newPassword2" :key="error">{{ error }}</div>
          </div>
        </md-field>
      </form>
      <div class="md-layout-item md-size-100 text-right">
        <md-button @click="closeDialog">Close</md-button>
        <md-button class="md-primary" @click="changePassword">Change</md-button>
      </div>
    </div>
  </md-dialog>
</template>

<script>
import Password from "vue-password-strength-meter";
import apiManager from "@/api-manager";
import { ref, computed } from "@vue/composition-api";
import snakeCaseObjectKeysToCamelCase from "@/utils/snakeCaseObjectKeysToCamelCase";
import camelCaseObjectKeysToSnakeCase from "@/utils/camelCaseObjectKeysToSnakeCase";

export default {
  name: "ChangePassword",
  components: {
    Password,
  },
  emits: ["changed"],
  props: ["show", "message", "title", "username"],
  setup(props, { emit }) {
    const passwordData = ref({
      oldPassword: "",
      newPassword1: "",
      newPassword2: "",
    });
    const errors = ref({
      oldPassword: "",
      newPassword1: "",
      newPassword2: "",
    });

    const resetErrors = () => {
      errors.value = {
        oldPassword: "",
        newPassword1: "",
        newPassword2: "",
      };
    };

    const resetPasswordInputs = () => {
      passwordData.value = {
        oldPassword: "",
        newPassword1: "",
        newPassword2: "",
      };
    };

    const openDialog = () => {
      resetErrors();
      resetPasswordInputs();
    };

    const closeDialog = () => {
      emit("close");
    };

    const changePassword = async () => {
      resetErrors();
      if (passwordData.value.oldPassword.length === 0) {
        errors.value.oldPassword = ["This field is required."];
        return;
      }

      let response;
      if (props.username) {
        response = await apiManager.changePasswordNoAuth({
          params: { ...camelCaseObjectKeysToSnakeCase(passwordData.value), username: props.username },
        });
      } else
        response = await apiManager.changePassword({
          params: camelCaseObjectKeysToSnakeCase(passwordData.value),
        });
      if (response.data.stat === "OK") {
        emit("changed");
      } else if (response.status === 401) {
        errors.value.oldPassword = ["Current password is incorrect."];
      } else {
        errors.value = snakeCaseObjectKeysToCamelCase(response.parsedError);
      }
    };

    const newPassword2NumErrors = computed(() => {
      return errors.value.newPassword2 ? errors.value.newPassword2.length : 0;
    });

    const oldPasswordClass = computed(() => {
      return {
        "md-invalid": errors.value.oldPassword,
      };
    });

    const newPasswordClass = computed(() => {
      return {
        "md-invalid": errors.value.newPassword1,
      };
    });
    const newPassword2Class = computed(() => {
      return {
        "md-invalid": errors.value.newPassword2,
      };
    });

    return {
      passwordData,
      errors,

      newPassword2NumErrors,

      resetErrors,
      resetPasswordInputs,

      openDialog,
      closeDialog,

      changePassword,

      // Computed Classes
      oldPasswordClass,
      newPasswordClass,
      newPassword2Class,
    };
  },
};
</script>

<style lang="scss" scoped>
.warning-text {
  text-align: justify !important;
}

.Password {
  max-width: 100%;
  padding-right: 0;
  padding-left: 0;
}

.md-layout-item.password-layout {
  margin-bottom: -4px;
}

.new-pass {
  margin-bottom: -8px;
}

.md-error.md-error-multi-line {
  position: absolute;
}

@for $i from 1 through 20 {
  .md-field.md-error-multi-line.md-error-#{$i}-lines {
    margin-bottom: calc(#{$i} * 35px);
  }
}
</style>
